// src/components/managed-sources/VerityPixelInstallPanel.tsx
/**
 * Install panel of a Verity Pixel source (plan 2026-10-02 verity pixel, D11 /
 * step 6.1): the snippet to paste, the live install status, the vault notice
 * (gate 3.5) and the site settings (consent + allowed domains).
 *
 * Shown in two places: the wizard's success screen right after the source is
 * created, and the Overview tab of the managed source drawer.
 *
 * Two reads, deliberately independent:
 *  - `GET /pixel-sites/{key}/status` is Postgres-only and answers in every
 *    environment, so it is polled every ~10 s while the install is still
 *    pending (`setting_up`, `waiting_for_first_event`) and never otherwise.
 *  - `GET /pixel-sites/{key}` carries the settings and the snippet. Where the
 *    environment has no collect host it answers 503 with a customer-readable
 *    detail: an INFO state ("not available yet"), never an error.
 *
 * Settings are editable by account admins only (the backend answers 403
 * otherwise); everyone else sees them read-only.
 *
 * Callers mount it with `key={siteKey}`: state is not reset when the prop
 * changes in place.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Code,
  CopyButton,
  Group,
  Loader,
  NumberInput,
  Paper,
  Radio,
  Select,
  Stack,
  TagsInput,
  Text,
  TextInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconAlertCircle,
  IconCheck,
  IconCopy,
  IconInfoCircle,
  IconShieldLock,
} from "@tabler/icons-react";

import { ApiError } from "@/api/client";
import { DocsLink } from "@/components/docs/DocsLink";
import {
  getPixelSite,
  getPixelSiteStatus,
  updatePixelSite,
  type PixelConsentMode,
  type PixelConsentPurpose,
  type PixelInstallState,
  type PixelSite,
  type PixelSiteStatus,
} from "@/api/pixelSites";
import { Link } from "@/routing/accountRouting";
import VerityPixelStorefrontCard from "@/components/managed-sources/VerityPixelStorefrontCard";
import { timeAgo } from "@/utils/timeAgo";
import { useLiveVisitors } from "@/hooks/useLiveVisitors";

/** How often the status is re-read while the install is still pending. */
export const PIXEL_STATUS_POLL_MS = 10_000;

/**
 * How long `setting_up` may last before the panel says it is taking longer.
 * The setup request hands activation to a background task that normally
 * finishes within seconds; the backend alerts ops at five minutes. Counted
 * from the first read that showed `setting_up`, because the status carries
 * no creation time.
 */
export const PIXEL_SLOW_ACTIVATION_MS = 3 * 60_000;

/** The states in which the customer is waiting for something to happen. */
const POLLING_STATES: ReadonlySet<PixelInstallState> = new Set([
  "setting_up",
  "waiting_for_first_event",
]);

export const PIXEL_STATE_LABELS: Record<PixelInstallState, string> = {
  setting_up: "Setting up",
  waiting_for_first_event: "Waiting for the first event",
  receiving: "Receiving events",
  quiet: "No events in the last day",
  paused: "Paused",
};

const PIXEL_STATE_COLORS: Record<PixelInstallState, string> = {
  setting_up: "blue",
  waiting_for_first_event: "yellow",
  receiving: "green",
  quiet: "orange",
  paused: "gray",
};

const CONSENT_MODE_LABELS: Record<PixelConsentMode, string> = {
  auto: "Automatic: read the choice from your consent banner",
  manual: "Manual: your website tells the pixel when a visitor consents",
};

const CONSENT_PURPOSE_OPTIONS: { value: PixelConsentPurpose; label: string }[] = [
  { value: "marketing", label: "Marketing" },
  { value: "analytics", label: "Analytics" },
];

type Props = {
  siteKey: string;
  isAccountAdmin: boolean;
  /** Test seam; defaults to PIXEL_STATUS_POLL_MS. */
  pollIntervalMs?: number;
  /** Test seam; defaults to PIXEL_SLOW_ACTIVATION_MS. */
  slowActivationMs?: number;
};

type SettingsForm = {
  allowedHosts: string[];
  consentMode: PixelConsentMode;
  consentPurpose: PixelConsentPurpose;
  onetrustAnalytics: string;
  onetrustMarketing: string;
  abandonedCartMinutes: number | string;
  imageHosts: string[];
};

function formFromSite(site: PixelSite): SettingsForm {
  return {
    allowedHosts: [...site.allowed_hosts],
    consentMode: site.consent_mode,
    consentPurpose: site.consent_purpose,
    onetrustAnalytics: site.onetrust_groups?.analytics ?? "",
    onetrustMarketing: site.onetrust_groups?.marketing ?? "",
    abandonedCartMinutes: site.abandoned_cart_minutes,
    imageHosts: [...(site.image_hosts ?? [])],
  };
}

function sameForm(a: SettingsForm, b: SettingsForm): boolean {
  return (
    a.consentMode === b.consentMode &&
    a.consentPurpose === b.consentPurpose &&
    a.onetrustAnalytics.trim() === b.onetrustAnalytics.trim() &&
    a.onetrustMarketing.trim() === b.onetrustMarketing.trim() &&
    Number(a.abandonedCartMinutes) === Number(b.abandonedCartMinutes) &&
    a.allowedHosts.length === b.allowedHosts.length &&
    a.allowedHosts.every((h, i) => h === b.allowedHosts[i]) &&
    a.imageHosts.length === b.imageHosts.length &&
    a.imageHosts.every((h, i) => h === b.imageHosts[i])
  );
}

function errorMessage(e: unknown, fallback: string): string {
  return e instanceof Error && e.message ? e.message : fallback;
}

function formatEventTime(iso: string | null): string {
  if (!iso) return "No events yet";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "No events yet";
  return `${timeAgo(iso)} (${d.toLocaleString()})`;
}

export default function VerityPixelInstallPanel({
  siteKey,
  isAccountAdmin,
  pollIntervalMs = PIXEL_STATUS_POLL_MS,
  slowActivationMs = PIXEL_SLOW_ACTIVATION_MS,
}: Props) {
  // Guards every setState behind an await: the panel can unmount (drawer
  // closed, wizard closed) while a read is in flight.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // --- Live visitors (plan 2026-10-05, B3): this site's count, same poll as the Insights card ---
  const live = useLiveVisitors();
  const liveHere =
    live.status === "available"
      ? live.last?.by_site.find((x) => x.site_key === siteKey) ?? null
      : null;

  // --- Live status ---
  const [status, setStatus] = useState<PixelSiteStatus | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);

  const loadStatus = useCallback(
    () =>
      getPixelSiteStatus(siteKey)
        .then((s) => {
          if (!mounted.current) return;
          setStatus(s);
          setStatusError(null);
        })
        .catch((e: unknown) => {
          if (!mounted.current) return;
          setStatusError(errorMessage(e, "Could not load the pixel status."));
        }),
    [siteKey],
  );

  // First read; the interval below takes over while the install is pending.
  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  const shouldPoll = !!status && POLLING_STATES.has(status.state);
  useEffect(() => {
    if (!shouldPoll) return;
    const id = window.setInterval(() => {
      void loadStatus();
    }, pollIntervalMs);
    return () => window.clearInterval(id);
  }, [shouldPoll, loadStatus, pollIntervalMs]);

  // Activation runs in the background after setup. Never a countdown: when
  // the first attempt fails the retry is an hour away, so the slow state
  // promises no time.
  const activating = status?.state === "setting_up";
  const [activationSlow, setActivationSlow] = useState(false);
  useEffect(() => {
    if (!activating) return;
    const id = window.setTimeout(() => setActivationSlow(true), slowActivationMs);
    return () => window.clearTimeout(id);
  }, [activating, slowActivationMs]);

  // --- Settings + snippet ---
  const [site, setSite] = useState<PixelSite | null>(null);
  const [siteLoading, setSiteLoading] = useState(true);
  // 503: the environment has no collect host. The backend's detail is the
  // customer-facing explanation; rendered as information, not as a failure.
  const [siteUnavailable, setSiteUnavailable] = useState<string | null>(null);
  const [siteError, setSiteError] = useState<string | null>(null);
  const [form, setForm] = useState<SettingsForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getPixelSite(siteKey)
      .then((s) => {
        if (cancelled || !mounted.current) return;
        setSite(s);
        setForm(formFromSite(s));
      })
      .catch((e: unknown) => {
        if (cancelled || !mounted.current) return;
        if (e instanceof ApiError && e.status === 503) {
          setSiteUnavailable(
            errorMessage(e, "The Verity Pixel is not available in this environment yet."),
          );
        } else {
          setSiteError(errorMessage(e, "Could not load the pixel settings."));
        }
      })
      .finally(() => {
        if (!cancelled && mounted.current) setSiteLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [siteKey]);

  async function handleSave() {
    if (!form) return;
    setSaving(true);
    setSaveError(null);
    const analytics = form.onetrustAnalytics.trim();
    const marketing = form.onetrustMarketing.trim();
    try {
      const updated = await updatePixelSite(siteKey, {
        allowed_hosts: form.allowedHosts,
        consent_mode: form.consentMode,
        consent_purpose: form.consentPurpose,
        abandoned_cart_minutes: Number(form.abandonedCartMinutes),
        image_hosts: form.imageHosts,
        onetrust_groups:
          analytics || marketing
            ? { analytics: analytics || null, marketing: marketing || null }
            : null,
      });
      if (!mounted.current) return;
      setSite(updated);
      setForm(formFromSite(updated));
      notifications.show({
        title: "Pixel settings saved",
        message: "The pixel uses the new settings within a few minutes.",
        color: "green",
      });
    } catch (e) {
      if (!mounted.current) return;
      setSaveError(errorMessage(e, "Could not save the pixel settings."));
    } finally {
      if (mounted.current) setSaving(false);
    }
  }

  const dirty = !!site && !!form && !sameForm(form, formFromSite(site));
  const showOneTrust =
    !!form &&
    (form.consentMode === "auto" ||
      !!form.onetrustAnalytics.trim() ||
      !!form.onetrustMarketing.trim());

  return (
    <Stack gap="md" data-testid="verity-pixel-install-panel">
      {/* ---- Status ---- */}
      <Paper withBorder radius="md" p="md">
        <Stack gap="xs">
          <Group justify="space-between">
            <Text size="sm" c="dimmed">Pixel status</Text>
            {status ? (
              <Badge
                variant="light"
                color={PIXEL_STATE_COLORS[status.state] ?? "gray"}
                data-testid="pixel-state-badge"
              >
                {PIXEL_STATE_LABELS[status.state] ?? status.state}
              </Badge>
            ) : statusError ? null : (
              <Loader size="xs" />
            )}
          </Group>
          {status && (
            <Group justify="space-between">
              <Text size="sm" c="dimmed">Last event</Text>
              <Text size="sm" fw={500}>{formatEventTime(status.last_event_at)}</Text>
            </Group>
          )}
          {liveHere && (
            <Text size="sm" data-testid="pixel-live-visitors">
              {liveHere.visitors.toLocaleString("en-US")}
              {live.last?.truncated ? "+" : ""}{" "}
              {liveHere.visitors === 1 ? "visitor" : "visitors"} on your site right now
            </Text>
          )}
          {status?.warehouse_through && (
            <Group justify="space-between">
              <Text size="sm" c="dimmed">In your warehouse through</Text>
              <Text size="sm" fw={500}>{status.warehouse_through}</Text>
            </Group>
          )}
          {activating && (
            <Text size="xs" c="dimmed" data-testid="pixel-activating">
              {activationSlow
                ? "Activating website tracking is taking longer than usual. It continues in the background, so you can install the snippet and close this window."
                : "Activating website tracking. This usually takes less than a minute. You can install the snippet now; events are recorded once tracking is active."}
            </Text>
          )}
          {status?.state === "waiting_for_first_event" && (
            <Text size="xs" c="dimmed">
              Open your website in a browser and accept the cookie banner. The first
              event shows up here within about a minute.
            </Text>
          )}
          {statusError && (
            <Alert color="red" icon={<IconAlertCircle size={16} />} title="Could not load the pixel status">
              {statusError}
            </Alert>
          )}
        </Stack>
      </Paper>

      {/* ---- Gate 3.5: no vault, no e-mail identification ---- */}
      {status && !status.email_identification_available && (
        <Alert
          color="grape"
          icon={<IconShieldLock size={16} />}
          title="E-mail identification needs the PII vault"
        >
          <Stack gap="xs">
            <Text size="sm">
              The pixel still records page activity. Sign-ins are not linked to
              your customers until your account&apos;s secure data vault (PII vault)
              is active.
            </Text>
            {isAccountAdmin && (
              <Anchor component={Link} to="/settings/account?tab=data-warehouse" size="sm" fw={600}>
                Set up your PII vault
              </Anchor>
            )}
          </Stack>
        </Alert>
      )}

      {/* ---- Snippet ---- */}
      <Stack gap="xs">
        <Text fw={600} size="sm">Install the pixel</Text>
        <DocsLink slug="data-management/sources/connecting/verity-pixel">Read the Verity Pixel setup guide</DocsLink>
        {siteLoading && (
          <Group gap="sm">
            <Loader size="sm" />
            <Text size="sm" c="dimmed">Loading the pixel settings…</Text>
          </Group>
        )}
        {siteUnavailable && (
          <Alert
            color="blue"
            icon={<IconInfoCircle size={16} />}
            title="The snippet is not available yet"
            data-testid="pixel-snippet-unavailable"
          >
            {siteUnavailable}
          </Alert>
        )}
        {siteError && (
          <Alert color="red" icon={<IconAlertCircle size={16} />} title="Could not load the pixel settings">
            {siteError}
          </Alert>
        )}
        {site && (
          <>
            {site.storefront_id && (
              <Alert
                color="blue"
                icon={<IconInfoCircle size={16} />}
                data-testid="pixel-snippet-shopify-note"
              >
                Your Shopify store does not need this snippet: turn on page tracking under
                Shopify storefront.
              </Alert>
            )}
            {site.storefront_id && (
              <Text size="xs" c="dimmed" fw={600}>
                Only needed for themes that do not support app embeds
              </Text>
            )}
            <Text size="sm">
              Paste this snippet into the {"<head>"} of every page of your website.
            </Text>
            <Code block data-testid="pixel-snippet">{site.snippet}</Code>
            <Group>
              <CopyButton value={site.snippet} timeout={2000}>
                {({ copied, copy }) => (
                  <Button
                    size="xs"
                    variant="light"
                    color={copied ? "teal" : undefined}
                    leftSection={copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                    onClick={copy}
                  >
                    {copied ? "Copied" : "Copy snippet"}
                  </Button>
                )}
              </CopyButton>
            </Group>
          </>
        )}
      </Stack>

      {/* ---- Settings ---- */}
      {site && form && (
        <Paper withBorder radius="md" p="md">
          <Stack gap="md">
            <Text fw={600} size="sm">Pixel settings</Text>

            {isAccountAdmin ? (
              <>
                <Radio.Group
                  label="Consent mode"
                  value={form.consentMode}
                  onChange={(v) => setForm({ ...form, consentMode: v as PixelConsentMode })}
                >
                  <Stack gap={6} mt={6}>
                    <Radio value="auto" label={CONSENT_MODE_LABELS.auto} />
                    <Radio value="manual" label={CONSENT_MODE_LABELS.manual} />
                  </Stack>
                </Radio.Group>

                <Select
                  label="Required consent"
                  description="The pixel records events only after the visitor gives this consent."
                  data={CONSENT_PURPOSE_OPTIONS}
                  value={form.consentPurpose}
                  onChange={(v) => {
                    if (v) setForm({ ...form, consentPurpose: v as PixelConsentPurpose });
                  }}
                  allowDeselect={false}
                  w={280}
                />

                {showOneTrust && (
                  <Stack gap={4}>
                    <Text size="sm" fw={500}>OneTrust groups</Text>
                    <Text size="xs" c="dimmed">
                      Only if your website uses OneTrust with its own group IDs. Leave
                      empty to use the OneTrust defaults.
                    </Text>
                    <Group gap="sm" align="flex-start">
                      <TextInput
                        label="OneTrust analytics group"
                        placeholder="C0002"
                        value={form.onetrustAnalytics}
                        onChange={(e) =>
                          setForm({ ...form, onetrustAnalytics: e.currentTarget.value })
                        }
                        w={200}
                      />
                      <TextInput
                        label="OneTrust marketing group"
                        placeholder="C0004"
                        value={form.onetrustMarketing}
                        onChange={(e) =>
                          setForm({ ...form, onetrustMarketing: e.currentTarget.value })
                        }
                        w={200}
                      />
                    </Group>
                  </Stack>
                )}

                <NumberInput
                  label="Abandoned cart after (minutes)"
                  description="A visitor's cart counts as abandoned when this many minutes pass after an add to cart without a purchase."
                  value={form.abandonedCartMinutes}
                  onChange={(v) => setForm({ ...form, abandonedCartMinutes: v })}
                  min={5}
                  max={10080}
                  step={5}
                  allowDecimal={false}
                  allowNegative={false}
                  w={280}
                />

                <TagsInput
                  label="Allowed domains"
                  description={`The pixel accepts events only from pages on these domains: ${site.website_host} or its subdomains.`}
                  placeholder="Add a domain, like shop.example.com"
                  value={form.allowedHosts}
                  onChange={(v) => setForm({ ...form, allowedHosts: v })}
                  splitChars={[",", " "]}
                  clearable
                />

                <TagsInput
                  label="Image domains"
                  description="Domains your product images are served from, such as your image CDN. Cart reminders show images only from these exact domains. Leave empty if you use Shopify: images then come from your store."
                  placeholder="Add a domain, like images.example-cdn.com"
                  value={form.imageHosts}
                  onChange={(v) => setForm({ ...form, imageHosts: v })}
                  splitChars={[",", " "]}
                  clearable
                />

                {saveError && (
                  <Alert color="red" icon={<IconAlertCircle size={16} />} title="Settings not saved">
                    {saveError}
                  </Alert>
                )}

                <Group justify="flex-end">
                  <Button onClick={handleSave} loading={saving} disabled={!dirty}>
                    Save settings
                  </Button>
                </Group>
              </>
            ) : (
              <>
                <Group justify="space-between" align="flex-start" wrap="nowrap">
                  <Text size="sm" c="dimmed">Consent mode</Text>
                  <Text size="sm" fw={500} ta="right">
                    {site.consent_mode === "auto" ? "Automatic" : "Manual"}
                  </Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">Required consent</Text>
                  <Text size="sm" fw={500}>
                    {site.consent_purpose === "marketing" ? "Marketing" : "Analytics"}
                  </Text>
                </Group>
                {site.onetrust_groups && (
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">OneTrust groups</Text>
                    <Text size="sm" fw={500}>
                      {[
                        site.onetrust_groups.analytics && `Analytics ${site.onetrust_groups.analytics}`,
                        site.onetrust_groups.marketing && `Marketing ${site.onetrust_groups.marketing}`,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </Text>
                  </Group>
                )}
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">Abandoned cart after</Text>
                  <Text size="sm" fw={500}>{site.abandoned_cart_minutes} minutes</Text>
                </Group>
                <Group justify="space-between" align="flex-start" wrap="nowrap">
                  <Text size="sm" c="dimmed">Allowed domains</Text>
                  <Text size="sm" fw={500} ta="right">{site.allowed_hosts.join(", ")}</Text>
                </Group>
                {(site.image_hosts ?? []).length > 0 && (
                  <Group justify="space-between" align="flex-start" wrap="nowrap">
                    <Text size="sm" c="dimmed">Image domains</Text>
                    <Text size="sm" fw={500} ta="right">{site.image_hosts.join(", ")}</Text>
                  </Group>
                )}
                <Text size="xs" c="dimmed">Only account admins can change these settings.</Text>
              </>
            )}
          </Stack>
        </Paper>
      )}

      {/* ---- Shopify storefront + checkout pixel (campaign measurement) ---- */}
      {site && (
        <VerityPixelStorefrontCard
          site={site}
          isAccountAdmin={isAccountAdmin}
          onSiteChange={(updated) => {
            setSite(updated);
            setForm(formFromSite(updated));
          }}
        />
      )}
    </Stack>
  );
}
